import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, Clock, Filter, X } from "lucide-react";

const API = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) + "/api";
const authHeader = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function fmtRelative(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
function fmtAbs(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
function staleClass(iso) {
  if (!iso) return "bg-slate-100 text-slate-400";
  const age = Date.now() - new Date(iso).getTime();
  if (age < 60 * 60 * 1000) return "bg-emerald-50";       // <1h — fresh
  if (age < 24 * 60 * 60 * 1000) return "bg-yellow-50";   // <1d
  if (age < 3 * 24 * 60 * 60 * 1000) return "bg-orange-50"; // <3d
  return "bg-rose-50";                                     // stale
}

export default function SuperAdminCronLedger() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ tenants: [], jobs: [], cells: {} });
  const [historyOpen, setHistoryOpen] = useState(null); // {job_id, tenant_id}
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/super-admin/cron-ledger/summary`, { headers: authHeader() });
      setSummary(r.data || { tenants: [], jobs: [], cells: {} });
    } catch (e) {
      toast.error("Failed to load cron ledger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadHistory = async (job_id, tenant_id) => {
    setHistoryLoading(true);
    setHistoryOpen({ job_id, tenant_id });
    try {
      const r = await axios.get(`${API}/super-admin/cron-ledger`, {
        params: { job_id, tenant_id, limit: 50 },
        headers: authHeader(),
      });
      setHistory(r.data?.rows || []);
    } catch (e) {
      toast.error("Failed to load run history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const categories = useMemo(() => {
    const s = new Set();
    (summary.jobs || []).forEach(j => s.add(j.category || "Other"));
    return ["all", ...Array.from(s).sort()];
  }, [summary.jobs]);

  const visibleJobs = useMemo(() => {
    if (categoryFilter === "all") return summary.jobs || [];
    return (summary.jobs || []).filter(j => (j.category || "Other") === categoryFilter);
  }, [summary.jobs, categoryFilter]);

  const currentLabel = useMemo(() => {
    if (!historyOpen) return "";
    const j = (summary.jobs || []).find(x => x.job_id === historyOpen.job_id);
    const t = (summary.tenants || []).find(x => x.id === historyOpen.tenant_id);
    return `${j?.label || historyOpen.job_id} — ${t?.name || historyOpen.tenant_id}`;
  }, [historyOpen, summary]);

  return (
    <div className="space-y-6" data-testid="super-admin-cron-ledger-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Cron Run Ledger</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Last run per background job per tenant. Cells are colour-coded by freshness so a missed
            monthly billing job (e.g. no dascricket run on the 1st) jumps out. Click any cell to see
            the last 50 runs for that job/tenant pair.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-xs text-slate-600 bg-slate-100 rounded px-2 py-1">
            <Filter className="w-3 h-3" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="bg-transparent text-xs focus:outline-none"
              data-testid="cron-ledger-category-filter"
            >
              {categories.map(c => <option key={c} value={c}>{c === "all" ? "All categories" : c}</option>)}
            </select>
          </div>
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-2 px-3 py-1.5 rounded bg-slate-900 text-white text-sm hover:bg-slate-800"
            data-testid="cron-ledger-refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </header>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200 inline-block" />&lt; 1 h</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-200 inline-block" />&lt; 24 h</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-100 border border-orange-200 inline-block" />&lt; 3 d</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-100 border border-rose-200 inline-block" />stale / never</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="cron-ledger-matrix">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left w-[300px]">Job</th>
                {summary.tenants.map(t => (
                  <th key={t.id} className="px-3 py-2 text-left" data-testid={`ledger-th-${t.id}`}>
                    <div className="font-semibold text-slate-800">{t.name}</div>
                    <div className="text-[10px] font-normal text-slate-500 normal-case">{t.slug || t.id}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleJobs.map((j) => (
                <tr key={j.job_id} className="border-t border-slate-100">
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-slate-800">{j.label}</div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">{j.category}</div>
                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">{j.job_id}</div>
                  </td>
                  {summary.tenants.map(t => {
                    const cell = summary.cells?.[j.job_id]?.[t.id];
                    if (!cell) {
                      return (
                        <td
                          key={t.id}
                          className="px-3 py-2 align-top cursor-pointer bg-rose-50 hover:bg-rose-100"
                          onClick={() => loadHistory(j.job_id, t.id)}
                          data-testid={`ledger-cell-${j.job_id}-${t.id}`}
                        >
                          <div className="flex items-center gap-1 text-xs text-rose-700">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            never
                          </div>
                          <div className="text-[10px] text-rose-500 mt-0.5">no run recorded</div>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={t.id}
                        className={`px-3 py-2 align-top cursor-pointer hover:brightness-95 ${cell.ok ? staleClass(cell.started_at) : "bg-rose-100"}`}
                        onClick={() => loadHistory(j.job_id, t.id)}
                        data-testid={`ledger-cell-${j.job_id}-${t.id}`}
                        title={fmtAbs(cell.started_at)}
                      >
                        <div className="flex items-center gap-1 text-xs font-medium">
                          {cell.ok ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          ) : (
                            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                          )}
                          <span className={cell.ok ? "text-slate-800" : "text-rose-700"}>
                            {fmtRelative(cell.started_at)}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-600 mt-0.5">
                          <span className="font-medium">{cell.rows_processed}</span> rows · {cell.duration_ms}ms
                        </div>
                        {!cell.ok && cell.error ? (
                          <div className="text-[10px] text-rose-600 mt-0.5 truncate max-w-[220px]" title={cell.error}>
                            {cell.error}
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!visibleJobs.length && (
                <tr>
                  <td colSpan={summary.tenants.length + 1} className="px-3 py-6 text-center text-sm text-slate-500">
                    No jobs match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* History drawer */}
      {historyOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/40 flex" onClick={() => setHistoryOpen(null)}>
          <div
            className="ml-auto w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="cron-ledger-history-drawer"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Run history</div>
                <div className="font-semibold text-slate-800 mt-0.5">{currentLabel}</div>
              </div>
              <button
                type="button"
                onClick={() => setHistoryOpen(null)}
                className="p-1 rounded hover:bg-slate-100"
                data-testid="cron-ledger-history-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {historyLoading ? (
              <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-4 py-2 text-left">Started</th>
                    <th className="px-4 py-2 text-left">Duration</th>
                    <th className="px-4 py-2 text-left">Rows</th>
                    <th className="px-4 py-2 text-left">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-slate-800">{fmtAbs(r.started_at)}</div>
                        <div className="text-[10px] text-slate-400">{fmtRelative(r.started_at)}</div>
                      </td>
                      <td className="px-4 py-2 text-slate-600">{r.duration_ms}ms</td>
                      <td className="px-4 py-2 text-slate-800 font-medium">{r.rows_processed}</td>
                      <td className="px-4 py-2">
                        {r.ok ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                            <CheckCircle2 className="w-3 h-3" /> ok
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5" title={r.error}>
                            <AlertTriangle className="w-3 h-3" /> {(r.error || "").slice(0, 60) || "failed"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!history.length && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-500">No runs recorded.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
